

















































































































































import { Component, Vue } from 'vue-property-decorator';
import { ValidationObserver, ValidationProvider } from 'vee-validate-3';

import ManagerService from '@/api/ManagerService';

import { IDropDownCardMenu } from '@/models/dropdownCardMenu';

import { setFocusOnFirstInput } from '@/utils/setFocusOnFirstInput';

import Page from '@/componentsV2/molecules/Page/Page.vue';
import DropDownCardMenu from '@/componentsV2/molecules/DropDownCardMenu/DropDownCardMenu.vue';
import Table from '@/componentsV2/organisms/Table/Table.vue';
import Typography from '@/componentsV2/atoms/Typography/Typography.vue';
import Input from '@/componentsV2/molecules/Inputs/TextField.vue';

import FilledButton from '@/components/molecules/FilledButton/FilledButton.vue';
import RowsTable from '@/components/Table/RowsTable.vue';
import ContextMenu from '@/components/ContextMenu/ContextMenu.vue';
import TableFilters from '@/components/molecules/TableFilters/TableFilters.vue';
import UserIcon from '@/components/molecules/UserIcon/UserIcon.vue';
import { EBusinessUserType } from '@/store/modules/businessTypes';
import segmentIo from '@/utils/segment.io';
import { isRemoteLoading } from '@/utils/remote-loading';

enum Roles {
  owner = 'Manager',
  manager = 'User'
}

type ContextMenuOption = Omit<IDropDownCardMenu, 'trigger'>;

type User = {
  id: string;
  name: string;
  mail: string;
  status: string;
  inviteId: string;
  businessId: string;
  role: string;
};

type FormData = {
  name: string;
  mail: string;
};

function defaultFormData(): FormData {
  return {
    name: '',
    mail: ''
  };
}

@Component({
  components: {
    ValidationObserver,
    ValidationProvider,
    Input,
    FilledButton,
    RowsTable,
    ContextMenu,
    Table,
    TableFilters,
    UserIcon,
    DropDownCardMenu,
    Page,
    Typography
  },
  $_veeValidate: { validator: 'new' }
})
export default class ManageTeam extends Vue {
  $refs!: {
    observer: InstanceType<typeof ValidationObserver>;
  };

  managerService = new ManagerService();

  list: User[] = [];
  roles = Roles;
  loading = false;
  selectedManagerId!: string;
  selectedBusinessId!: string;
  formData: FormData = defaultFormData();

  get tPrefix() {
    return 'business.manageTeam.list';
  }

  get isMobileDevice() {
    return this.$mobileCheck();
  }

  get contextMenuAcceptedOptions(): ContextMenuOption[] {
    return [
      {
        label: 'Transfer Management',
        key: 'transfer'
      },
      {
        label: 'Remove user',
        key: 'remove'
      }
    ];
  }

  get contextMenuPendingOptions(): ContextMenuOption[] {
    return [
      {
        label: 'Resend Invitation',
        key: 'resend'
      },
      {
        label: 'Remove Invitation',
        key: 'remove-invite'
      }
    ];
  }

  mounted() {
    this.loadList();
  }

  isOwner(user: User) {
    return user.role === Roles.owner;
  }

  isAccepted(user: User) {
    return user.status === 'accepted';
  }

  resetForm() {
    const formRef = this.$refs.observer;

    formRef.reset();
    this.formData = defaultFormData();

    const inputs = document.querySelectorAll<HTMLInputElement>(
      '.user-management__content__form input'
    );

    for (const input of inputs) {
      input.value = '';
    }
  }

  async onSubmit() {
    const formRef = this.$refs.observer;
    const formIsValid = await formRef.validate();

    if (!formIsValid) {
      setFocusOnFirstInput('input[aria-invalid="true"]');

      return;
    }

    this.$showLoaderWithLocker();

    try {
      this.loading = true;

      await this.managerService.sendInvitation(this.formData);

      this.$notification('Invitation sent');

      segmentIo.track('Manage Team Invitation Sent', {
        'Invitation Name': this.formData.name,
        'Invitation Email': this.formData.mail
      });

      this.loadList();
      this.$store.dispatch('business/GET_TEAM_MEMBERS');

      this.resetForm();

      setFocusOnFirstInput();
    } finally {
      this.loading = false;
      this.$hideLoader();
    }
  }

  getRole(slug: string, status: string) {
    if (status === 'pending') return '';

    if (slug === EBusinessUserType.OWNER) return 'Manager';

    return 'User';
  }

  handleContextMenuItemClick(option: ContextMenuOption, user: User) {
    const methods: Record<string, () => void> = {
      resend: () => {
        this.resend(user.mail);
      },
      'remove-invite': () => {
        this.removeInvite(user.inviteId);
      },
      remove: () => {
        this.selectedManagerId = user.id;
        this.selectedBusinessId = user.businessId;

        this.removeManager();
      },
      transfer: () => {
        this.selectedManagerId = user.id;

        this.openTransferConfirmation(user.name);
      }
    };

    if (!methods[option.key]) return;

    methods[option.key]();
  }

  formattedUsers(users: any[]): User[] {
    const formatted = users.map(user => ({
      id: user.manager.id,
      businessId: user.business.id,
      inviteId: user.managerInvite.id,
      name: user.manager.fullname,
      mail: user.managerInvite.mail,
      status: user.managerInvite.status,
      role: this.getRole(user.businessManager.slug, user.managerInvite.status)
    }));

    return formatted.sort((a, b) => (a.role === Roles.owner ? -1 : 1));
  }

  async loadList({ showLoader = true } = {}) {
    if (showLoader) {
      this.$showLoaderWithLocker();
    }

    try {
      this.loading = true;
      const { data } = await this.managerService.getTeamMembers();

      this.list = this.formattedUsers(data.items);
    } finally {
      this.loading = false;
      this.$hideLoader();
    }
  }

  async openTransferConfirmation(name: string) {
    const response = await this.$showConfirmDialog({
      title: 'Transfer management',
      description: `You are about to transfer the management to <b>${name}</b>.`,
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Transfer',
      align: 'left'
    });

    if (response !== 'confirm') return;

    this.transferOwnership();
  }

  async transferOwnership() {
    if (!confirm) return;

    this.$showLoaderWithLocker();

    try {
      await this.managerService.transferOwnership({
        manager: this.selectedManagerId
      });

      this.$notification('Management transferred');
      setTimeout(() => {
        if (isRemoteLoading()) {
          window.parent.location.assign('/');
        } else {
          window.location.assign('/');
        }
      }, 2000);
    } finally {
      this.$hideLoader();
    }
  }

  async removeManager() {
    this.$showLoaderWithLocker();

    try {
      await this.managerService.removeManager({
        manager: this.selectedManagerId,
        business: this.selectedBusinessId
      });

      segmentIo.track('Manage Team User Removed', {
        'User Id': this.selectedManagerId,
        'Business Id': this.selectedBusinessId
      });

      this.$notification('User removed');
      this.loadList();
    } finally {
      this.$hideLoader();
    }
  }

  async removeInvite(invite: string) {
    this.$showLoaderWithLocker();

    try {
      await this.managerService.removeInvitation(invite);

      this.$notification('Invitation removed');

      segmentIo.track('Manage Team Invitation Removed', {
        'Invitation Id': invite
      });
      this.loadList();
    } finally {
      this.$hideLoader();
    }
  }

  async resend(mail: string) {
    this.$showLoaderWithLocker();

    try {
      await this.managerService.sendInvitation({ mail });

      this.$notification('Invitation resent');

      segmentIo.track('Manage Team Invitation Resent', {
        'Invitation Email': mail
      });
      this.loadList();
    } finally {
      this.$hideLoader();
    }
  }
}
